/**
 * Enhanced Post Composer Component
 * 
 * Modern, modular post composition component that leverages the Enhanced Platform Service.
 * Based on ComposePostRefactored.js structure but with Enhanced Platform Service integration.
 * 
 * Features:
 * - Unified multi-platform posting with Enhanced Platform Service
 * - Real-time validation and feedback
 * - Enhanced accessibility support
 * - Comprehensive analytics tracking
 * - Thread support for compatible platforms
 * - TikTok integration
 * - Media upload and validation
 * - Scheduling functionality
 */

import React, { useEffect, useRef, useState } from "react";
import { Box, styled, useTheme, useMediaQuery, CircularProgress } from "@mui/material";
import { useAuth } from "../providers/AuthProvider";
import { useLocation } from 'react-router-dom';
import { useContentBuckets } from "../providers/ContentBucketsProvider";
import { useTokenValidation } from "../providers/TokenValidationProvider";
import { useStatus } from "../providers/MsgStatusProvider";
import { useFeatureAccess } from "../hooks/useFeatureAccess";
import { useAnalytics } from "../context/AnalyticsContext";
import { useTracking } from "../hooks/useTracking";
import { validateVideoDuration } from '../utils/video-util';
import { validateFile } from '../utils/fileValidation';
import { validateAltText } from '../utils/altTextValidation';
import { ComposePostEvents, EnhancedPlatformEvents } from '../services/analytics/events';
import { createEnhancedPlatformService } from '../services/enhancedPlatformService';
import dayjs from "dayjs";
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { 
    getTimezoneDisplayInfo, 
    validateScheduleDate, 
    formatScheduledDate,
    getUserTimezone 
} from '../utils/schedulingHelpers';

// Extend dayjs with timezone support
dayjs.extend(utc);
dayjs.extend(timezone);

// Modular Components
import {
    PostTypeSelector,
    MediaPreview,
    ContentBucketSelector,
    PlatformInputs,
    PostActions
} from './compose';
import OptimalTimesInsightPanel from './compose/OptimalTimesInsightPanel';

// Existing components we'll still use
import AccountSelection from './AccountSelection';
import MediaControls from './MediaControls';
import ThreadComposer from './ThreadComposer';
import ImageGalleryDialog from "./ImageGalleryDialog";

/**
 * Enhanced Post Composer Component
 * Refactored version with Enhanced Platform Service integration
 * Following PostDetailsModal.js clean patterns
 */

const Root = styled(Box)(({ theme }) => ({
    padding: theme.spacing(3),
    margin: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    border: '1px solid ' + theme.palette.divider,
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(1),
    }
}));

const ContentContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(3),
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'center',
    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        gap: theme.spacing(2),
    }
}));

const EnhancedPostComposer = ({ 
    editMode = false, 
    postData = null, 
    readOnly = false, 
    limitedMode = false,
    rescheduleOnly = false,
    onScheduleUpdate = null 
}) => {
    const { user, token, isLoading: authLoading } = useAuth();
    const [activeTab, setActiveTab] = useState(0);
    const [postContent, setPostContent] = useState("");
    const { canCreateMedia } = useFeatureAccess();
    const { buckets, fetchBuckets } = useContentBuckets();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { setStatusMessage, setIsAlertOpen, setStatusType } = useStatus();
    const { trackEvent } = useAnalytics();
    const { trackWithError, trackWithSuccess } = useTracking();
    const { checkBeforePosting } = useTokenValidation();
    const location = useLocation();
    
    // Get navigation state and merge with props
    const navigationState = location.state || {};
    const isEditMode = editMode || navigationState.editMode || false;
    const postDataToEdit = postData || navigationState.postData || null;
    const isRescheduleOnly = rescheduleOnly || navigationState.rescheduleOnly || false;
    
    // Add comprehensive debugging
    console.log('[EnhancedPostComposer] Props received:', {
        editMode,
        postData,
        readOnly,
        limitedMode,
        rescheduleOnly,
        hasPostData: !!postData,
        postDataId: postData?.id
    });
    
    console.log('[EnhancedPostComposer] Navigation state:', navigationState);
    console.log('[EnhancedPostComposer] Final edit mode values:', {
        isEditMode,
        postDataToEdit,
        isRescheduleOnly,
        hasPostData: !!postDataToEdit,
        postDataId: postDataToEdit?.id
    });

    const [postType, setPostType] = useState('single'); // 'single' or 'thread'
    const [selectedAccounts, setSelectedAccounts] = useState([]);
    const [userPlatforms, setUserPlatforms] = useState([]);

    // Content & Media State
    const [selectedBucket, setSelectedBucket] = useState('');
    const [useCommonText, setUseCommonText] = useState(false);
    const [commonText, setCommonText] = useState('');
    const [postImg, setPostImg] = useState({ url: '', type: 'png' });
    const [imgAltText, setImgAltText] = useState('');
    const [uploadedFile, setUploadedFile] = useState(null);
    const [videoError, setVideoError] = useState('');

    // Scheduling State
    const [postDate, setPostDate] = useState('');
    const [showCalendar, setShowCalendar] = useState(false);

    // UI State
    const [open, setOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [currentThreadEditIndex, setCurrentThreadEditIndex] = useState(null);
    
    // Timezone & Schedule Validation State
    const [timezoneInfo, setTimezoneInfo] = useState(null);
    const [scheduleValidation, setScheduleValidation] = useState(null);

    // Thread State
    const [threadData, setThreadData] = useState([]);

    // TikTok State
    const [tiktokFormValidity, setTiktokFormValidity] = useState(true);
    const [tiktokCreatorInfo, setTiktokCreatorInfo] = useState(null);

    // Enhanced Platform Service State
    const [validationResults, setValidationResults] = useState(null);
    const [platformCapabilities, setPlatformCapabilities] = useState(null);
    const [serviceHealth, setServiceHealth] = useState({ service_healthy: true, healthy: true });

    // Accessibility State
    const [accessibilitySettings, setAccessibilitySettings] = useState({
        requireAltText: true, // Default to requiring alt text for enterprise compliance
        enforceAltTextQuality: false,
        showAltTextGuidance: false
    });

    // Refs
    const fileInputRef = useRef(null);
    const threadComposerRef = useRef(null);

    // Initialize Enhanced Platform Service
    const enhancedService = React.useMemo(() => {
        if (!token || !user?.[0]) return null;
        const trackingHandler = { trackEvent, trackWithError, trackWithSuccess };
        const service = createEnhancedPlatformService({ user, token }, trackingHandler, {
            defaultMaxRetries: 3,
            defaultBaseDelay: 1000,
            defaultTimeout: 30000,
            enableCaching: true,
            logLevel: 'info'
        });
        
        trackEvent(EnhancedPlatformEvents.SERVICE_INITIALIZED.name, {
            userId: user[0]?.id
        });
        
        return service;
    }, [user, token, trackEvent, trackWithError, trackWithSuccess]);

    // Initialize platforms and fetch buckets
    useEffect(() => {
                 if (user?.[0]?.userprofile?.social_accounts) {
            const platforms = user[0].userprofile.social_accounts.map(account => account.platform);
            const uniquePlatforms = [...new Set(platforms)];
            setUserPlatforms(uniquePlatforms);
        }
        fetchBuckets();
    }, [user, fetchBuckets]);
    
    // Initialize edit mode data
    useEffect(() => {
        if (isEditMode && postDataToEdit && user?.[0]?.userprofile?.social_accounts) {
            console.log('[EnhancedPostComposer] Edit mode initialization starting');
            console.log('[EnhancedPostComposer] Post data received:', postDataToEdit);
            console.log('[EnhancedPostComposer] User social accounts:', user[0].userprofile.social_accounts);
            
            try {
                // Track edit mode entry
                trackEvent(EnhancedPlatformEvents.EDIT_MODE_INITIALIZED.name, {
                    postId: postDataToEdit.id,
                    status: postDataToEdit.status,
                    scheduledDate: postDataToEdit.scheduled_post_date,
                    platforms: postDataToEdit.platform_details?.map(p => p.platform) || []
                });
                
                // Set post type
                const isThread = postDataToEdit.is_thread || postDataToEdit.thread_data?.length > 0;
                setPostType(isThread ? 'thread' : 'single');
                console.log('[EnhancedPostComposer] Post type set to:', isThread ? 'thread' : 'single');
                
                // Set content
                if (isThread && postDataToEdit.thread_data) {
                    setThreadData(postDataToEdit.thread_data);
                    console.log('[EnhancedPostComposer] Thread data set');
                } else {
                    setCommonText(postDataToEdit.post_text || '');
                    setUseCommonText(true);
                    console.log('[EnhancedPostComposer] Common text set to:', postDataToEdit.post_text);
                }
                
                // Set media
                if (postDataToEdit.img_url) {
                    // Better media type detection
                    let mediaType = 'png'; // default
                    if (postDataToEdit.media_type) {
                        mediaType = postDataToEdit.media_type;
                    } else if (postDataToEdit.img_url.includes('.mp4')) {
                        mediaType = 'mp4';
                    } else if (postDataToEdit.img_url.includes('.gif')) {
                        mediaType = 'gif';
                    }
                    
                    setPostImg({
                        url: postDataToEdit.img_url,
                        type: mediaType
                    });
                    setImgAltText(postDataToEdit.img_alt_text || '');
                }
                
                // Set schedule
                if (postDataToEdit.scheduled_post_date) {
                    // Convert to local datetime-local format
                    const scheduledDate = dayjs(postDataToEdit.scheduled_post_date);
                    const userTz = getUserTimezone(user[0].userprofile);
                    const localDate = scheduledDate.tz(userTz).format('YYYY-MM-DDTHH:mm');
                    setPostDate(localDate);
                    setShowCalendar(true);
                    // Call the callback if provided
                    if (typeof onScheduleUpdate === 'function') {
                        onScheduleUpdate(localDate);
                    }
                }
                
                // Set content buckets
                if (postDataToEdit.content_buckets?.length > 0) {
                    setSelectedBucket(postDataToEdit.content_buckets[0].id);
                }
                
                // Pre-select accounts and set messages
                const allAccounts = user[0].userprofile.social_accounts;
                const preSelectedAccounts = [];
                console.log('[EnhancedPostComposer] All user accounts:', allAccounts);
                
                // If platform_details exists and has data
                if (postDataToEdit.platform_details && postDataToEdit.platform_details.length > 0) {
                    console.log('[EnhancedPostComposer] Platform details found:', postDataToEdit.platform_details);
                    postDataToEdit.platform_details.forEach(platformDetail => {
                        console.log('[EnhancedPostComposer] Processing platform detail:', platformDetail);
                        const matchingAccount = allAccounts.find(acc => 
                            acc.platform.toLowerCase() === platformDetail.platform.toLowerCase()
                        );
                        
                        if (matchingAccount) {
                            const accountData = {
                                accountId: matchingAccount.id,
                                username: matchingAccount.username,
                                platform: matchingAccount.platform,
                                account_img: matchingAccount.profile_pic_url || matchingAccount.account_img,
                                isActive: true,
                                message: platformDetail.platform_specific_text || postDataToEdit.post_text || ''
                            };
                            console.log('[EnhancedPostComposer] Adding pre-selected account:', accountData);
                            preSelectedAccounts.push(accountData);
                        } else {
                            console.warn('[EnhancedPostComposer] No matching account found for platform:', platformDetail.platform);
                        }
                    });
                } else {
                    // Fallback: If no platform_details, try to infer from the post
                    console.log('[EnhancedPostComposer] No platform_details found, using fallback');
                    // You might want to select some default accounts here based on your business logic
                }
                
                setSelectedAccounts(preSelectedAccounts);
                
                // If all accounts have the same message, use common text
                if (preSelectedAccounts.length > 0) {
                    const firstMessage = preSelectedAccounts[0].message;
                    const allSameMessage = preSelectedAccounts.every(acc => acc.message === firstMessage);
                    if (allSameMessage) {
                        setCommonText(firstMessage);
                        setUseCommonText(true);
                    }
                } else if (postDataToEdit.post_text) {
                    // If no accounts were pre-selected but we have post text, still show it
                    setCommonText(postDataToEdit.post_text);
                    setUseCommonText(true);
                }
            } catch (error) {
                console.error('[EnhancedPostComposer] Error initializing edit mode:', error);
                setStatusMessage('Error loading post data for editing');
                setStatusType('error');
                setIsAlertOpen(true);
            }
        }
    }, [isEditMode, postDataToEdit, user, trackEvent, onScheduleUpdate, setStatusMessage, setStatusType, setIsAlertOpen]);
    
    // Initialize timezone info
    useEffect(() => {
        if (user?.[0]?.userprofile) {
            const tzInfo = getTimezoneDisplayInfo(user[0].userprofile);
            console.log('[EnhancedPostComposer] Setting timezone info:', tzInfo);
            setTimezoneInfo(tzInfo);
        }
    }, [user]);

    // Initialize service capabilities
    useEffect(() => {
        if (enhancedService) {
            const initService = async () => {
                try {
                    const capabilities = await enhancedService.getPlatformCapabilities();
                    setPlatformCapabilities(capabilities);
                    
                    const health = await enhancedService.getServiceHealth();
                    setServiceHealth(health);
                    
                    trackEvent(EnhancedPlatformEvents.CAPABILITIES_FETCHED.name, {
                        platformCount: Object.keys(capabilities).length
                    });
                } catch (error) {
                    console.error('Failed to initialize Enhanced Platform Service:', error);
                    trackWithError(EnhancedPlatformEvents.SERVICE_HEALTH_CHECK, error);
                }
            };
            
            initService();
        }
    }, [enhancedService, trackEvent, trackWithError]);

    // Real-time validation - Disabled for now to use original API
    // TODO: Re-implement validation using original backend API format
    // useEffect(() => {
    //     // Validation logic will be re-implemented later
    // }, [selectedAccounts, postType, threadData, useCommonText, commonText, postImg, imgAltText, showCalendar]);

    // Cleanup object URLs on unmount
    useEffect(() => {
        return () => {
            if (postImg.url) {
                URL.revokeObjectURL(postImg.url);
            }
        };
    }, []);

    // Get thread-capable accounts
    const hasThreadCapableAccounts = user?.[0]?.userprofile?.social_accounts?.some(account => 
        ['twitter', 'bluesky'].includes(account.platform.toLowerCase())
    ) || false;

    const threadSupportedAccounts = selectedAccounts.filter(account => 
        ['twitter', 'bluesky'].includes(account.platform.toLowerCase())
    );

    // Get available accounts based on post type
    const getAvailableAccounts = () => {
        const allAccounts = user?.[0]?.userprofile?.social_accounts || [];
        
        if (postType === 'thread') {
            return allAccounts.filter(account => 
                ['twitter', 'bluesky'].includes(account.platform.toLowerCase())
            );
        }
        
        return allAccounts;
    };

    // Validation
    const isAnyMessageSet = () => {
        if (postType === 'thread') {
            return (threadData || []).some(post => post?.text && post.text.trim() !== '');
        }
        return (selectedAccounts || []).some(account => account.message && account.message.trim() !== '');
    };

    const hasCharacterLimitExceeded = () => {
        if (!platformCapabilities) return false;

        if (postType === 'thread') {
            return (threadData || []).some(post => {
                return selectedAccounts.some(account => {
                    const limits = platformCapabilities[account.platform];
                    return limits && post?.text && post.text.length > limits.maxLength;
                });
            });
        }

        return selectedAccounts.some(account => {
            const limits = platformCapabilities[account.platform];
            const content = useCommonText ? commonText : (account.message || '');
            return limits && content.length > limits.maxLength;
        });
    };

    const isThreadValid = () => {
        if (postType !== 'thread') return true;
        if (!threadData || threadData.length < 2) return false;
        if (threadSupportedAccounts.length === 0) return false;
        return threadData.every(post => post?.text && post.text.trim() !== '');
    };

    // Alt text validation
    const validateAccessibility = () => {
        if (!postImg.url || postImg.type === 'mp4') {
            return { isValid: true, message: '' };
        }

        if (!accessibilitySettings.requireAltText) {
            return { isValid: true, message: '' };
        }

        const platforms = (selectedAccounts || []).map(account => account.platform);
        const altValidation = validateAltText(imgAltText, platforms, {
            requireAltText: accessibilitySettings.requireAltText,
            enforceQuality: accessibilitySettings.enforceAltTextQuality,
            minQualityScore: 50
        });

        if (!altValidation.isValid) {
            return { 
                isValid: false, 
                message: 'Alt text is required for accessibility compliance' 
            };
        }

        return { isValid: true, message: '' };
    };

    const getValidationMessage = () => {
        if (postType === 'thread' && (!threadData || threadData.length < 2)) {
            return 'Thread must contain at least 2 posts';
        }
        if (postType === 'single' && !isAnyMessageSet()) {
            return 'Please enter some content';
        }
        if (hasCharacterLimitExceeded()) {
            return 'Some posts exceed character limits';
        }
        
        // Check accessibility compliance
        const accessibility = validateAccessibility();
        if (!accessibility.isValid) {
            return accessibility.message;
        }
        
        return '';
    };

    // Validate schedule date when it changes
    useEffect(() => {
        if (postDate && user?.[0]?.userprofile) {
            const subscriptionTier = user[0].userprofile.subscription_tier || 'free';
            const validation = validateScheduleDate(postDate, subscriptionTier);
            setScheduleValidation(validation);
            
                    // Track validation failures
        if (!validation.isValid) {
            trackEvent(EnhancedPlatformEvents.VALIDATION_ERROR_DETAILED.name, {
                validationType: 'schedule_date',
                error: validation.message,
                scheduledDate: postDate,
                subscriptionTier: subscriptionTier
            });
        }
        } else {
            setScheduleValidation(null);
        }
    }, [postDate, user, trackEvent]);

    // Early return if auth is not loaded yet - AFTER all hooks
    if (!user?.[0]?.userprofile?.social_accounts) {
        return (
            <Root>
                <Box display="flex" justifyContent="center" alignItems="center" p={4} flexDirection="column" gap={2}>
                    <CircularProgress />
                    <Box>Loading composer...</Box>
                </Box>
            </Root>
        );
    }

    // Event Handlers
    const handlePostTypeChange = (newType) => {
        // Don't allow changing post type in limited edit mode
        if (limitedMode) {
            setStatusMessage('Cannot change post type when post is scheduled within 15 minutes');
            setStatusType('warning');
            setIsAlertOpen(true);
            return;
        }
        
        setPostType(newType);
        
        trackEvent(EnhancedPlatformEvents.POST_TYPE_CHANGED.name, {
            oldType: postType,
            newType: newType,
            platformCount: selectedAccounts.length
        });

        if (newType === 'single') {
            setThreadData([]);
        } else if (newType === 'thread') {
            const threadSupportedSelected = selectedAccounts.filter(account => 
                ['twitter', 'bluesky'].includes(account.platform.toLowerCase())
            );
            setSelectedAccounts(threadSupportedSelected);
            setThreadData([]);
        }
    };

    const handleAccountChange = (accountId, username, platform, account_img) => {
        // Don't allow changing platforms in limited edit mode
        if (limitedMode) {
            setStatusMessage('Cannot change platforms when post is scheduled within 15 minutes');
            setStatusType('warning');
            setIsAlertOpen(true);
            return;
        }
        
        try {
            const accountIndex = selectedAccounts.findIndex(account => account.accountId === accountId);

            if (accountIndex > -1) {
                const newSelectedAccounts = selectedAccounts.filter(account => account.accountId !== accountId);
                setSelectedAccounts(newSelectedAccounts);
                
                trackEvent(ComposePostEvents.SOCIAL_ACCOUNT_DESELECTED.name, {
                    platform, username, remainingAccounts: newSelectedAccounts.length
                });
            } else {
                const newAccount = {
                    accountId, username, platform, account_img,
                    isActive: true,
                    message: useCommonText ? commonText : ''
                };
                
                const newSelectedAccounts = [...selectedAccounts, newAccount];
                setSelectedAccounts(newSelectedAccounts);
                
                trackEvent(ComposePostEvents.SOCIAL_ACCOUNT_SELECTED.name, {
                    platform, username, totalAccounts: newSelectedAccounts.length
                });

                trackEvent(EnhancedPlatformEvents.PLATFORM_SELECTION_CHANGED.name, {
                    platforms: newSelectedAccounts.map(acc => acc.platform),
                    platformCount: newSelectedAccounts.length
                });
            }
        } catch (error) {
            console.error('Error in handleAccountChange:', error);
            setStatusMessage('Error selecting account. Please try again.');
            setStatusType('error');
            setIsAlertOpen(true);
        }
    };

    const handleInputChange = (e, accountId) => {
        try {
            const updatedAccounts = (selectedAccounts || []).map(account => {
                if (account.accountId === accountId) {
                    return {
                        ...account,
                        message: e.target.value,
                    };
                }
                return account;
            });
            setSelectedAccounts(updatedAccounts);

            if (useCommonText && selectedAccounts.length > 0 && selectedAccounts[0].accountId === accountId) {
                setCommonText(e.target.value);
            }
        } catch (error) {
            console.error('Error updating account message:', error);
            setStatusMessage('Error updating message. Please try again.');
            setStatusType('error');
            setIsAlertOpen(true);
        }
    };

    const handleCommonTextChange = (e) => {
        const newText = e.target.value;
        setCommonText(newText);

        if (useCommonText) {
            const updatedAccounts = selectedAccounts.map(account => ({
                ...account,
                message: newText
            }));
            setSelectedAccounts(updatedAccounts);
        }
    };

    const handleToggleCommonText = (checked) => {
        setUseCommonText(checked);
        trackEvent(ComposePostEvents.SAME_TEXT_CHECKBOX.name);

        const updatedAccounts = (selectedAccounts || []).map(account => ({
            ...account,
            message: checked ? commonText : account.message || ''
        }));
        setSelectedAccounts(updatedAccounts);
    };

    const handleClearAll = () => {
        setCommonText('');
        const clearedAccounts = (selectedAccounts || []).map(account => ({
            ...account,
            message: ''
        }));
        setSelectedAccounts(clearedAccounts);
        trackEvent(ComposePostEvents.POST_CLEAR_ALL_CLICKED.name);
    };

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        try {
            // Get platform names for validation
            const selectedPlatforms = (selectedAccounts || []).map(account => account.platform);
            
            // Validate file size and format first
            const fileValidation = validateFile(file, selectedPlatforms);
            
            if (!fileValidation.isValid) {
                setStatusMessage(fileValidation.errors[0]);
                setStatusType('error');
                setIsAlertOpen(true);
                
                // Still create preview URL for user to see the file
                const url = URL.createObjectURL(file);
                const fileType = file.type.split('/')[1];
                setPostImg({
                    url,
                    type: fileType === 'mp4' ? 'mp4' : 
                          fileType === 'gif' ? 'gif' : 'png'
                });
                setUploadedFile(file);
                return;
            }

            // If file validation passed but it's a video, also validate duration
            const isVideo = file.type.startsWith('video/');
            if (isVideo) {
                const { isValid, error } = await validateVideoDuration(
                    file, 
                    selectedPlatforms.map(p => p.toUpperCase()),
                    tiktokCreatorInfo
                );
                
                if (!isValid) {
                    setVideoError(error);
                    setStatusMessage(`Video validation failed: ${error}`);
                    setStatusType('error');
                    setIsAlertOpen(true);
                    
                    const url = URL.createObjectURL(file);
                    setPostImg({ url, type: 'mp4' });
                    setUploadedFile(file);
                    return;
                }
                setVideoError('');
            }

            // File is valid - create preview and set state
            const url = URL.createObjectURL(file);
            const fileType = file.type.split('/')[1];
            
            setPostImg({
                url,
                type: fileType === 'mp4' ? 'mp4' : 
                      fileType === 'gif' ? 'gif' : 'png'
            });
            setUploadedFile(file);
            
            trackEvent(EnhancedPlatformEvents.MEDIA_ATTACHMENT_ADDED.name, {
                fileType: fileType,
                fileSize: file.size,
                platforms: selectedPlatforms
            });
            
            // Show success message for large files
            if (fileValidation.warnings.length > 0) {
                setStatusMessage(`File uploaded successfully. ${fileValidation.warnings[0]}`);
                setStatusType('warning');
                setIsAlertOpen(true);
            } else {
                setStatusMessage(`File uploaded successfully (${fileValidation.formattedSize})`);
                setStatusType('success');
                setIsAlertOpen(true);
            }
            
        } catch (error) {
            console.error('Error handling file upload:', error);
            setStatusMessage('Error processing file upload');
            setStatusType('error');
            setIsAlertOpen(true);
            setVideoError('Failed to process file');
            trackWithError(EnhancedPlatformEvents.MEDIA_ATTACHMENT_ADDED, error);
        }
    };

    const handleRemoveMedia = () => {
        if (postImg.url) {
            URL.revokeObjectURL(postImg.url);
        }
        setPostImg({ url: '', type: 'png' });
        setImgAltText('');
        setUploadedFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }

        trackEvent(EnhancedPlatformEvents.MEDIA_ATTACHMENT_REMOVED.name);
    };

    // Function to assign post to bucket
    const assignPostToBucket = async (postId, bucketId) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}buckets/api/post-bucket-assignments/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Token ${token}`
                },
                body: JSON.stringify({
                    post: postId,
                    bucket: bucketId
                })
            });

            if (!response.ok) {
                throw new Error('Failed to assign post to bucket');
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error assigning post to bucket:', error);
            throw error;
        }
    };

    const clearForm = () => {
        setCommonText('');
        setUseCommonText(false);
        
        const clearedAccounts = (selectedAccounts || []).map(account => ({
            ...account,
            message: ''
        }));
        setSelectedAccounts(clearedAccounts);

        if (postImg.url) {
            URL.revokeObjectURL(postImg.url);
        }
        setPostImg({ url: '', type: 'png' });
        setImgAltText('');
        setUploadedFile(null);
        
        setPostDate('');
        setShowCalendar(false);
        setSelectedBucket('');
        setValidationResults(null);

        trackEvent(ComposePostEvents.POST_CLEAR_ALL_CLICKED.name);
    };

    const handlePostNow = async () => {
        setIsSubmitting(true);
        
        try {
            // Check token validation before posting
            // const canPost = await checkBeforePosting();
            const canPost = true;
            if (!canPost) {
                setIsSubmitting(false);
                return;
            }

            trackEvent(EnhancedPlatformEvents.MULTI_PLATFORM_POST_START.name, {
                platformCount: selectedAccounts.length,
                postType: postType,
                hasMedia: !!postImg.url,
                isScheduled: false
            });

            const activeAccounts = selectedAccounts.filter(account => account.message);
            const formDataArray = [];

            for (const account of activeAccounts) {
                let formData = new FormData();
                formData.append('post_text', account.message);
                formData.append('is_scheduled_post', false);
                formData.append('social_account_id', account.accountId);
                formData.append('platform', account.platform);
                formData.append('img_alt_text', imgAltText || '');
                
                // If we have an uploaded file, append it
                if (uploadedFile) {
                    // Convert file to base64
                    const base64 = await new Promise((resolve) => {
                        const reader = new FileReader();
                        reader.onloadend = () => resolve(reader.result);
                        reader.readAsDataURL(uploadedFile);
                    });

                    formData.append('media_file', base64);
                    formData.append('media_type', postImg.type);
                } else {
                    formData.append('image_url', postImg.url);
                    formData.append('media_type', postImg.type);
                }
                formDataArray.push(Object.fromEntries(formData));
            }

            // Add template and media tracking to the first item (post-level data)
            if (formDataArray.length > 0) {
                // Add template tracking if image came from a template
                if (postImg.source_template_id) {
                    formDataArray[0].source_template_id = postImg.source_template_id;
                }
                
                // Add generated media tracking if image was generated
                if (postImg.generated_media_id) {
                    formDataArray[0].attached_media_ids = [postImg.generated_media_id];
                }
            }

            // Only proceed with API call if there are posts
            if (formDataArray.length > 0) {
                const url = process.env.REACT_APP_API_BASE_URL + 'post/posts/';
                const response = await fetch(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Token ${token}`
                    },
                    body: JSON.stringify(formDataArray)
                });

                const data = await response.json();
                if (data.success) {
                    // Assign posts to bucket if a bucket is selected
                    if (selectedBucket && data.post_ids) {
                        try {
                            // Assign each created post to the selected bucket
                            const bucketAssignmentPromises = data.post_ids.map(postId =>
                                assignPostToBucket(postId, selectedBucket)
                            );
                            await Promise.all(bucketAssignmentPromises);
                            
                            const bucketName = buckets.find(b => b.id === parseInt(selectedBucket))?.name || 'selected bucket';
                            setStatusMessage(`${data.message} Posts assigned to "${bucketName}".`);
                        } catch (bucketError) {
                            console.error('Error assigning posts to bucket:', bucketError);
                            setStatusMessage(`${data.message} Warning: Could not assign posts to bucket.`);
                        }
                    } else {
                        setStatusMessage(data.message);
                    }
                    
                    setIsAlertOpen(true);
                    setStatusType('success');
                    trackWithSuccess(EnhancedPlatformEvents.MULTI_PLATFORM_POST_SUCCESS, {
                        platformCount: selectedAccounts.length,
                        hasMedia: !!postImg.url,
                        hasBucket: !!selectedBucket
                    });
                    clearForm();
                } else {
                    trackWithError(EnhancedPlatformEvents.MULTI_PLATFORM_POST_FAILURE, new Error(data.message));
                    setIsAlertOpen(true);
                    setStatusMessage(data.message);
                    setStatusType('error');
                }
            }
        } catch (error) {
            console.error('Error posting:', error);
            setStatusMessage(error.message || 'Error creating post');
            setStatusType('error');
            setIsAlertOpen(true);
            
            trackWithError(EnhancedPlatformEvents.MULTI_PLATFORM_POST_FAILURE, error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSchedulePost = async () => {
        setIsSubmitting(true);
        
        try {
            // Check token validation before scheduling post
            // const canPost = await checkBeforePosting();
            const canPost = true;
            if (!canPost) {
                setIsSubmitting(false);
                return;
            }

            trackEvent(EnhancedPlatformEvents.MULTI_PLATFORM_POST_START.name, {
                platformCount: (selectedAccounts || []).length,
                postType: postType,
                hasMedia: !!postImg.url,
                isScheduled: true,
                isEdit: isEditMode
            });

            const activeAccounts = selectedAccounts.filter(account => account.message);
            
            if (isEditMode && postDataToEdit) {
                // Update existing post
                const formData = {
                    post_text: useCommonText ? commonText : activeAccounts[0]?.message || '',
                    is_scheduled_post: true,
                    scheduled_post_date: postDate,
                    img_alt_text: imgAltText,
                    // Include platform-specific data
                    platform_details: activeAccounts.map(account => ({
                        platform: account.platform,
                        social_account_id: account.accountId,
                        platform_specific_text: account.message
                    }))
                };
                
                // Add media if present
                if (postImg.url) {
                    formData.image_url = postImg.url;
                    formData.media_type = postImg.type;
                }
                
                // Add bucket if selected
                if (selectedBucket) {
                    formData.content_bucket_id = selectedBucket;
                }
                
                const url = `${process.env.REACT_APP_API_BASE_URL}post/posts/${postDataToEdit.id}/`;
                const response = await fetch(url, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Token ${token}`
                    },
                    body: JSON.stringify(formData)
                });

                const data = await response.json();
                if (response.ok) {
                    // Format success message with scheduled date
                    const userTz = getUserTimezone(user[0].userprofile);
                    const formattedDate = formatScheduledDate(postDate, userTz);
                    let successMessage = `Post updated and scheduled for ${formattedDate}`;
                    
                    setStatusMessage(successMessage);
                    setIsAlertOpen(true);
                    setStatusType('success');
                    
                    // Auto-dismiss after 5 seconds
                    setTimeout(() => {
                        setIsAlertOpen(false);
                    }, 5000);

                    trackWithSuccess(EnhancedPlatformEvents.POST_UPDATE_SUCCESS, {
                        postId: postDataToEdit.id,
                        platformCount: selectedAccounts.length,
                        scheduledDate: postDate,
                        hasMedia: !!postImg.url
                    });
                    
                    // Navigate back to calendar page after successful update
                    setTimeout(() => {
                        window.location.href = '/calendar';
                    }, 1500);
                } else {
                    throw new Error(data.error || data.message || 'Failed to update post');
                }
            } else {
                // Create new scheduled post (existing logic)
            const formDataArray = [];

            for (const account of activeAccounts) {
                let formData = {
                    post_text: account.message,
                    is_scheduled_post: true,
                    scheduled_post_date: postDate,
                    social_account_id: account.accountId,
                    platform: account.platform,
                    image_url: postImg.url,
                    media_type: postImg.type,
                    img_alt_text: imgAltText
                };

                formDataArray.push(formData);
            }

            // Add template and media tracking to the first item (post-level data)
            if (formDataArray.length > 0) {
                // Add template tracking if image came from a template
                if (postImg.source_template_id) {
                    formDataArray[0].source_template_id = postImg.source_template_id;
                }
                
                // Add generated media tracking if image was generated
                if (postImg.generated_media_id) {
                    formDataArray[0].attached_media_ids = [postImg.generated_media_id];
                }
            }

            const url = process.env.REACT_APP_API_BASE_URL + 'post/tweet/scheduled/';
            const response = await fetch(url, {
                method: 'post',
                headers: {
                    'Content-Type': 'application/json',
                        'Authorization': `Token ${token}`
                },
                body: JSON.stringify(formDataArray)
            });

            const data = await response.json();
            if (data.success) {
                    // Format success message with scheduled date (without timezone per requirements)
                    const userTz = getUserTimezone(user[0].userprofile);
                    const formattedDate = formatScheduledDate(postDate, userTz);
                    let successMessage = `Post scheduled for ${formattedDate}`;
                    
                // Assign scheduled posts to bucket if a bucket is selected
                if (selectedBucket && data.post_ids) {
                    try {
                        // Assign each created post to the selected bucket
                        const bucketAssignmentPromises = data.post_ids.map(postId =>
                            assignPostToBucket(postId, selectedBucket)
                        );
                        await Promise.all(bucketAssignmentPromises);
                        
                        const bucketName = buckets.find(b => b.id === parseInt(selectedBucket))?.name || 'selected bucket';
                            successMessage += `. Posts assigned to "${bucketName}".`;
                    } catch (bucketError) {
                        console.error('Error assigning scheduled posts to bucket:', bucketError);
                            successMessage += `. Warning: Could not assign posts to bucket.`;
                    }
                    }
                    
                    setStatusMessage(successMessage);
                    setIsAlertOpen(true);
                    setStatusType('success');
                    
                    // Auto-dismiss after 5 seconds
                    setTimeout(() => {
                        setIsAlertOpen(false);
                    }, 5000);

                trackWithSuccess(EnhancedPlatformEvents.MULTI_PLATFORM_POST_SUCCESS, {
                    platformCount: selectedAccounts.length,
                    scheduledDate: postDate,
                    hasMedia: !!postImg.url,
                    hasBucket: !!selectedBucket,
                    isScheduled: true
                });
                clearForm();
            } else {
                trackWithError(EnhancedPlatformEvents.MULTI_PLATFORM_POST_FAILURE, new Error(data.message));
                setIsAlertOpen(true);
                setStatusMessage(data.message);
                setStatusType('error');
                }
            }
        } catch (error) {
            console.error('Error scheduling:', error);
            setStatusMessage(error.message || 'Error scheduling post');
            setStatusType('error');
            setIsAlertOpen(true);
            
            trackWithError(EnhancedPlatformEvents.MULTI_PLATFORM_POST_FAILURE, error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleThreadPost = async () => {
        setIsSubmitting(true);
        
        try {
            trackEvent(EnhancedPlatformEvents.THREAD_POST_START.name, {
                threadLength: (threadData || []).length,
                platforms: (threadSupportedAccounts || []).map(acc => acc.platform),
                isScheduled: false // Immediate posting
            });

            // Import thread media handler
            const { threadMediaHandler } = await import('../utils/threadMediaHandler');

            // Validate media against platform rules
            const selectedPlatforms = selectedAccounts.map(acc => acc.platform);
            const mediaValidation = threadMediaHandler.validateThreadMedia(threadData, selectedPlatforms);
            
            if (!mediaValidation.valid) {
                setStatusMessage(mediaValidation.errors.join('; '));
                setStatusType('error');
                setIsAlertOpen(true);
                setIsSubmitting(false);
                return;
            }

            // Prepare platforms data
            const platformsData = selectedAccounts.map(account => ({
                platform: account.platform,
                social_account_id: account.accountId
            }));

            // Prepare thread data with media files
            const formData = await threadMediaHandler.prepareThreadData(
                threadData,
                platformsData,
                false  // Not scheduled
            );

            console.log('Sending thread with media support');

            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}post/threads/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Token ${token}`
                    // Don't set Content-Type - let browser set it for multipart
                },
                body: formData
            });

            const data = await response.json();
            console.log('Thread response:', data);
            
            if (data.success) {
                // Assign thread posts to bucket if a bucket is selected
                if (selectedBucket && data.post_ids) {
                    try {
                        // Assign each created post to the selected bucket
                        const bucketAssignmentPromises = data.post_ids.map(postId =>
                            assignPostToBucket(postId, selectedBucket)
                        );
                        await Promise.all(bucketAssignmentPromises);
                        
                        const bucketName = buckets.find(b => b.id === parseInt(selectedBucket))?.name || 'selected bucket';
                        setStatusMessage(`Thread posted successfully! Posts assigned to "${bucketName}".`);
                    } catch (bucketError) {
                        console.error('Error assigning thread posts to bucket:', bucketError);
                        setStatusMessage('Thread posted successfully! Warning: Could not assign posts to bucket.');
                    }
                } else {
                    setStatusMessage('Thread posted successfully!');
                }
                
                setStatusType('success');
                setIsAlertOpen(true);
                clearForm();
                trackWithSuccess(EnhancedPlatformEvents.THREAD_POST_SUCCESS, {
                    threadLength: (threadData || []).length,
                    platformCount: selectedAccounts.length,
                    hasBucket: !!selectedBucket
                });
            } else {
                setStatusMessage(data.message || 'Failed to post thread');
                setStatusType('error');
                setIsAlertOpen(true);
                trackWithError(EnhancedPlatformEvents.THREAD_POST_FAILURE, new Error(data.message));
            }
        } catch (error) {
            console.error('Error posting thread:', error);
            setStatusMessage(error.message || 'Error posting thread');
            setStatusType('error');
            setIsAlertOpen(true);
            
            trackWithError(EnhancedPlatformEvents.THREAD_POST_FAILURE, error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleThreadSchedule = async () => {
        setIsSubmitting(true);
        
        try {
            // Check token validation before scheduling thread
            // const canPost = await checkBeforePosting();
            const canPost = true;
            if (!canPost) {
                setIsSubmitting(false);
                return;
            }

            trackEvent(EnhancedPlatformEvents.THREAD_POST_START.name, {
                threadLength: (threadData || []).length,
                platforms: (threadSupportedAccounts || []).map(acc => acc.platform),
                isScheduled: true // Scheduled posting
            });

            // Import thread media handler
            const { threadMediaHandler } = await import('../utils/threadMediaHandler');

            // Validate media against platform rules
            const selectedPlatforms = selectedAccounts.map(acc => acc.platform);
            const mediaValidation = threadMediaHandler.validateThreadMedia(threadData, selectedPlatforms);
            
            if (!mediaValidation.valid) {
                setStatusMessage(mediaValidation.errors.join('; '));
                setStatusType('error');
                setIsAlertOpen(true);
                setIsSubmitting(false);
                return;
            }

            // Prepare platforms data
            const platformsData = selectedAccounts.map(account => ({
                platform: account.platform,
                social_account_id: account.accountId
            }));

            // Prepare thread data with media files
            const formData = await threadMediaHandler.prepareThreadData(
                threadData,
                platformsData,
                true,  // Scheduled
                postDate
            );

            console.log('Sending scheduled thread with media support');

            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}post/threads/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Token ${token}`
                    // Don't set Content-Type - let browser set it for multipart
                },
                body: formData
            });

            const data = await response.json();
            console.log('Scheduled thread response:', data);
            
            if (data.success) {
                // Format success message with scheduled date
                const userTz = getUserTimezone(user[0].userprofile);
                const formattedDate = formatScheduledDate(postDate, userTz);
                let successMessage = `Thread scheduled for ${formattedDate}`;
                
                // Assign thread posts to bucket if a bucket is selected
                if (selectedBucket && data.post_ids) {
                    try {
                        const bucketAssignmentPromises = data.post_ids.map(postId =>
                            assignPostToBucket(postId, selectedBucket)
                        );
                        await Promise.all(bucketAssignmentPromises);
                        
                        const bucketName = buckets.find(b => b.id === parseInt(selectedBucket))?.name || 'selected bucket';
                        successMessage += `. Posts assigned to "${bucketName}".`;
                    } catch (bucketError) {
                        console.error('Error assigning scheduled thread posts to bucket:', bucketError);
                        successMessage += `. Warning: Could not assign posts to bucket.`;
                    }
                }
                
                setStatusMessage(successMessage);
                setStatusType('success');
                setIsAlertOpen(true);
                
                // Auto-dismiss after 5 seconds
                setTimeout(() => {
                    setIsAlertOpen(false);
                }, 5000);
                
                clearForm();
                trackWithSuccess(EnhancedPlatformEvents.THREAD_POST_SUCCESS, {
                    threadLength: (threadData || []).length,
                    platformCount: selectedAccounts.length,
                    hasBucket: !!selectedBucket,
                    isScheduled: true
                });
            } else {
                setStatusMessage(data.message || 'Failed to schedule thread');
                setStatusType('error');
                setIsAlertOpen(true);
                trackWithError(EnhancedPlatformEvents.THREAD_POST_FAILURE, new Error(data.message));
            }
        } catch (error) {
            console.error('Error scheduling thread:', error);
            setStatusMessage(error.message || 'Error scheduling thread');
            setStatusType('error');
            setIsAlertOpen(true);
            
            trackWithError(EnhancedPlatformEvents.THREAD_POST_FAILURE, error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const isFormValid = () => {
        const basicValid = postType === 'thread' ? isThreadValid() : isAnyMessageSet() && !hasCharacterLimitExceeded();
        const accessibilityValid = validateAccessibility().isValid;
        
        return basicValid && accessibilityValid;
    };
    
    const validationMessage = getValidationMessage();

    return (
        <Root>
            {/* Service Health Indicator */}
            {!(serviceHealth.healthy || serviceHealth.service_healthy) && (
                <Box sx={{ mb: 2, p: 1, bgcolor: 'warning.light', borderRadius: 1 }}>
                    Enhanced posting features may be limited due to service issues.
                </Box>
            )}

            {/* Post Type Selector */}
            <PostTypeSelector
                postType={postType}
                onPostTypeChange={handlePostTypeChange}
                hasThreadCapableAccounts={hasThreadCapableAccounts}
                threadSupportedAccounts={threadSupportedAccounts}
            />

            {/* Account Selection */}
            <AccountSelection
                accounts={getAvailableAccounts()}
                selectedAccounts={selectedAccounts}
                handleAccountChange={handleAccountChange}
            />

            {/* Optimal Times Insight Panel */}
            {selectedAccounts.length > 0 && (
                <OptimalTimesInsightPanel
                    selectedPlatforms={selectedAccounts.map(acc => acc.platform)}
                    onOptimalTimeSelect={(optimalTime) => {
                        // Create a scheduled date from the optimal time
                        const scheduledDate = dayjs()
                            .hour(optimalTime.hour || 0)
                            .minute(optimalTime.minute || 0)
                            .second(0)
                            .format('YYYY-MM-DDTHH:mm');
                        
                        setPostDate(scheduledDate);
                        setShowCalendar(true);
                        
                        // Track optimal time selection from insight panel
                        trackEvent(EnhancedPlatformEvents.PERSONALIZED_TIME_SELECTED.name, {
                            platform: selectedAccounts[0]?.platform,
                            hour: optimalTime.hour,
                            minute: optimalTime.minute,
                            confidence: optimalTime.confidence_score,
                            source: 'insight_panel'
                        });
                    }}
                />
            )}

            {/* Content Bucket Selection */}
            {selectedAccounts.length > 0 && (
                <ContentBucketSelector
                    selectedBucket={selectedBucket}
                    onBucketChange={setSelectedBucket}
                    buckets={buckets}
                    disabled={isSubmitting}
                />
            )}

            {/* Enhanced Validation Display - Temporarily disabled */}
            {/* TODO: Re-implement validation display with original backend API */}

            {/* Content Area */}
            {postType === 'thread' ? (
                <ThreadComposer
                    selectedAccounts={threadSupportedAccounts}
                    onThreadChange={setThreadData}
                    initialThread={threadData}
                    onImageGalleryOpen={(postIndex) => {
                        setCurrentThreadEditIndex(postIndex);
                        setOpen(true);
                    }}
                    ref={threadComposerRef}
                />
            ) : (
                <ContentContainer>
                    <MediaPreview
                        postImg={postImg}
                        imgAltText={imgAltText}
                        onAltTextChange={setImgAltText}
                        videoError={videoError}
                        uploadedFile={uploadedFile}
                        selectedPlatforms={selectedAccounts}
                        showValidation={true}
                        requireAltText={accessibilitySettings.requireAltText}
                        enforceAltTextQuality={accessibilitySettings.enforceAltTextQuality}
                        showAltTextGuidance={accessibilitySettings.showAltTextGuidance}
                    />
                    
                    <PlatformInputs
                        selectedAccounts={selectedAccounts}
                        useCommonText={useCommonText}
                        commonText={commonText}
                        onCommonTextChange={handleCommonTextChange}
                        onToggleCommonText={handleToggleCommonText}
                        onInputChange={handleInputChange}
                        onClearAll={handleClearAll}
                        isSubmitting={isSubmitting}
                        platformCapabilities={platformCapabilities}
                        onTikTokPostComplete={(result) => {
                            if (result.success) {
                                setStatusMessage(result.message);
                                setStatusType('success');
                                setIsAlertOpen(true);
                                clearForm();
                            }
                        }}
                        onTikTokError={(error) => {
                            setStatusMessage(error);
                            setStatusType('error');
                            setIsAlertOpen(true);
                        }}
                        onTikTokFormValidityChange={setTiktokFormValidity}
                        tiktokFormValidity={tiktokFormValidity}
                    />
                </ContentContainer>
            )}

            {/* Media Controls & Actions */}
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                mt: 3
            }}>
                {postType === 'single' && (
                    <MediaControls
                        hasMedia={!!postImg.url}
                        disabled={selectedAccounts.length === 0}
                        onFileUpload={handleFileUpload}
                        onImageGalleryOpen={() => setOpen(true)}
                        onRemoveMedia={handleRemoveMedia}
                        acceptedFileTypes="image/*, video/mp4"
                        showGenerateMedia={true}
                        compact={false}
                    />
                )}

                <PostActions
                    postType={postType}
                    showCalendar={showCalendar}
                    onShowCalendar={setShowCalendar}
                    postDate={postDate}
                    onDateChange={setPostDate}
                    isSubmitting={isSubmitting}
                    onSchedulePost={handleSchedulePost}
                    onPostNow={handlePostNow}
                    onThreadPost={handleThreadPost}
                    onThreadSchedule={handleThreadSchedule}
                    isValid={isFormValid()}
                    validationMessage={validationMessage}
                    disabled={selectedAccounts.length === 0}
                    timezoneInfo={timezoneInfo}
                    scheduleValidation={scheduleValidation}
                    selectedPlatforms={selectedAccounts.map(acc => acc.platform)}
                    postContent={useCommonText ? commonText : (selectedAccounts[0]?.message || '')}
                    editPostId={isEditMode && postDataToEdit ? postDataToEdit.id : null}
                />
            </Box>

            {/* Image Gallery Dialog */}
            <ImageGalleryDialog 
                open={open} 
                onClose={() => {
                    setOpen(false);
                    setCurrentThreadEditIndex(null);
                }} 
                setPostImg={(imageData) => {
                    if (currentThreadEditIndex !== null && threadComposerRef.current) {
                        threadComposerRef.current.updatePostMedia(currentThreadEditIndex, {
                            url: imageData.url,
                            type: imageData.type || 'png',
                            file: null
                        });
                    } else {
                        setPostImg(imageData);
                    }
                }} 
                postImg={
                    currentThreadEditIndex !== null && threadData[currentThreadEditIndex]
                        ? {
                            url: threadData[currentThreadEditIndex].media_url || '',
                            type: threadData[currentThreadEditIndex].media_type || 'png'
                        }
                        : postImg
                }
                creatorInfo={tiktokCreatorInfo}
            />
        </Root>
    );
};

export default EnhancedPostComposer; 